import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import LocationIndicator from './LocationIndicator';
import UserProfileDropdown from './UserProfileDropdown';
import { useAuth } from '../../contexts/AuthContext';
import { getUnreadCount, subscribeToUnreadCount, unsubscribeChannel } from '../../services/notificationsService';
import { getUserConversations } from '../../services/messagingService';



const Header = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const notificationChannelRef = useRef(null);
  
  // DEVELOPMENT MODE: Set to true to bypass authentication for testing
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [userType, setUserType] = useState('worker'); // Simulated user type for testing

  const navigationItems = [
    { label: 'Find Work', path: '/find-work', icon: 'Search', requiresAuth: false },
    { label: 'Find Service Provider', path: '/service-providers', icon: 'Users', requiresAuth: false },
    { label: 'Post Job', path: '/post-job', icon: 'PlusCircle', requiresAuth: false },
    { label: 'Messages', path: '/messages', icon: 'MessageSquare', requiresAuth: false },
    { label: 'Notifications', path: '/notifications', icon: 'Bell', requiresAuth: false },
    { label: 'Dashboard', path: '/user-dashboard', icon: 'LayoutDashboard', requiresAuth: true },
    { label: 'Earnings', path: '/earnings', icon: 'DollarSign', requiresAuth: true },
    { label: 'Worker Profile', path: '/worker-profile', icon: 'User', requiresAuth: true },
    { label: 'Admin', path: '/admin-dashboard', icon: 'Shield', requiresAuth: true, adminOnly: true },
  ];

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const handleAuthToggle = (type) => {
    setIsAuthenticated(true);
    setUserType(type);
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUserType(null);
  };

  // Get userId from auth context or use dev mode fallback
  const { user, isAdmin, signOut } = useAuth();
  const userId = user?.id || (import.meta.env?.VITE_DEV_MODE === 'true' ? '00000000-0000-0000-0000-000000000001' : null);

  // Load unread counts and subscribe to real-time updates
  useEffect(() => {
    if (!userId) return;

    const loadUnreadCounts = async () => {
      try {
        const [notifCount, conversations] = await Promise.all([
          getUnreadCount(),
          getUserConversations(userId)
        ]);
        
        setUnreadNotifications(notifCount);
        
        // Calculate total unread messages
        const totalUnread = conversations?.reduce((sum, conv) => sum + (conv?.unreadCount || 0), 0);
        setUnreadMessages(totalUnread);
      } catch (error) {
        console.error('Error loading unread counts:', error);
      }
    };

    loadUnreadCounts();

    // Subscribe to notification count updates
    notificationChannelRef.current = subscribeToUnreadCount(userId, (count) => {
      setUnreadNotifications(count);
    });

    return () => {
      if (notificationChannelRef?.current) {
        unsubscribeChannel(notificationChannelRef?.current);
      }
    };
  }, [userId]);

  const filteredNavItems = navigationItems?.filter(item => {
    if (item?.requiresAuth && !isAuthenticated) return false;
    if (item?.adminOnly && !isAdmin) return false;
    return true;
  });

  return (
    <header className="fixed top-0 left-0 right-0 z-header bg-white shadow-[0_2px_8px_rgba(0,0,0,0.06)] h-[56px] lg:h-[56px]">
      <nav className="flex items-center justify-between h-full px-[12px] lg:px-[16px] bg-white">
        <div className="flex items-center gap-[16px]">
          <Link 
            to="/home-landing" 
            className="flex items-center gap-[10px] transition-smooth hover:opacity-80"
            aria-label="DISKARTE Home"
          >
            <img 
              src="/assets/images/LOGO-1771655664486.png" 
              alt="DISKARTE Logo" 
              className="w-[44px] h-[44px] object-contain"
            />
            <span className="text-xl font-heading font-bold text-[#FF6A00] [text-shadow:_-1px_-1px_0_#fff,_1px_-1px_0_#fff,_-1px_1px_0_#fff,_1px_1px_0_#fff]">
              DISKARTE
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-[4px]">
            {filteredNavItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`
                  flex items-center gap-[6px] px-[12px] py-[8px] rounded-md
                  transition-smooth font-medium text-[13px] font-semibold relative
                  ${isActivePath(item?.path)
                    ? 'bg-[#FF6A00] text-white'
                    : 'text-[#1F2937] hover:bg-[#1E73BE] hover:text-white'
                  }
                `}
              >
                <Icon name={item?.icon} size={15} />
                <span className="text-xs text-center font-medium">{item?.label}</span>
                {item?.path === '/notifications' && unreadNotifications > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {unreadNotifications > 9 ? '9+' : unreadNotifications}
                  </span>
                )}
                {item?.path === '/messages' && unreadMessages > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#1E73BE] text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-[12px]">
          <LocationIndicator className="hidden md:flex" />

          {!isAuthenticated ? (
            <div className="hidden lg:flex items-center gap-[12px]">
              <Button
                variant="outline"
                size="default"
                onClick={() => window.location.href = '/authentication'}
                iconName="LogIn"
                iconPosition="left"
                className="border-[#1E73BE] text-[#1E73BE] hover:bg-[#1E73BE]/10"
              >
                Log In
              </Button>
              <Button
                variant="default"
                size="default"
                onClick={() => window.location.href = '/authentication'}
                iconName="UserPlus"
                iconPosition="left"
                className="bg-[#FF6A00] hover:bg-[#FF7A1A]"
              >
                Sign Up
              </Button>
            </div>
          ) : (
            <UserProfileDropdown 
              userType={userType} 
              onLogout={handleLogout}
              userId={userId}
            />
          )}

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-[8px] rounded-md hover:bg-muted transition-smooth"
            aria-label="Toggle mobile menu"
            aria-expanded={isMobileMenuOpen}
          >
            <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
          </button>
        </div>
      </nav>
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-card border-t border-border">
          <div className="px-[16px] py-[12px] space-y-[6px]">
            {filteredNavItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`
                  flex items-center gap-[12px] px-[18px] py-[12px] rounded-md
                  transition-smooth font-medium text-[15px] relative
                  ${isActivePath(item?.path)
                    ? 'bg-[#FF6A00] text-white'
                    : 'text-[#1F2937] hover:bg-[#F9FAFB]'
                  }
                `}
              >
                <Icon name={item?.icon} size={20} />
                <span>{item?.label}</span>
                {item?.path === '/notifications' && unreadNotifications > 0 && (
                  <span className="ml-auto w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {unreadNotifications > 9 ? '9+' : unreadNotifications}
                  </span>
                )}
                {item?.path === '/messages' && unreadMessages > 0 && (
                  <span className="ml-auto w-6 h-6 bg-[#1E73BE] text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </span>
                )}
              </Link>
            ))}

            <div className="pt-[12px] border-t border-border mt-[12px]">
              <LocationIndicator className="mb-[12px]" />
              
              {!isAuthenticated ? (
                <div className="space-y-[8px]">
                  <Button
                    variant="outline"
                    size="default"
                    fullWidth
                    onClick={() => window.location.href = '/authentication'}
                    iconName="LogIn"
                    iconPosition="left"
                    className="border-[#1E73BE] text-[#1E73BE] hover:bg-[#1E73BE]/10"
                  >
                    Log In
                  </Button>
                  <Button
                    variant="default"
                    size="default"
                    fullWidth
                    onClick={() => window.location.href = '/authentication'}
                    iconName="UserPlus"
                    iconPosition="left"
                    className="bg-[#FF6A00] hover:bg-[#FF7A1A]"
                  >
                    Sign Up
                  </Button>
                </div>
              ) : (
                <UserProfileDropdown 
                  userType={userType}
                  onLogout={handleLogout}
                  isMobile
                />
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;